// ────────────────────────────────────────────────────────────────────
// WHICH END OF THE INSPECTION LIST IS WHICH.
//
// The wire order is NEWEST FIRST — `server/integrity/assemble.group_inspections`
// sorts on the way out and its docstring calls that a contract, pinned by
// tests/foundation/test_integrity_assemble.py::test_events_come_back_newest_first.
//
// Nothing in this screen may read `inspections[0]` or `inspections[length - 1]`
// and call it the current inspection. Positional reads are how the screen came
// to mark the OLDEST row as current and describe the NEWEST reading as the
// baseline: both components agreed with each other and disagreed with the
// server, and the fixtures agreed with the components, so the suite was green
// on a screen telling an engineer the opposite of the truth.
//
// So the direction is asked for by NAME, and answered from the DATES rather
// than from a position. Re-ordering the wire cannot break a caller here, and
// no test can pass by matching a wrong array order.
//
// Dates are ISO `YYYY-MM-DD`, which sorts lexicographically — the same
// property `assemble.py` leans on. That is the only reason string compare is
// enough; a display format would need parsing.
// ────────────────────────────────────────────────────────────────────
import type { InspectionEvent } from "./types";

/** Oldest → newest. A copy: the record is never mutated. */
export function oldestFirst(events: readonly InspectionEvent[]): InspectionEvent[] {
  return [...events].sort((a, b) => a.date.localeCompare(b.date));
}

/** Newest → oldest. The wire order, restated explicitly so a caller that wants
 *  it says so rather than inheriting it. */
export function newestFirst(events: readonly InspectionEvent[]): InspectionEvent[] {
  return [...events].sort((a, b) => b.date.localeCompare(a.date));
}

/** THE CURRENT INSPECTION: the one with the newest date, or null on an asset
 *  with no inspections at all. Never "the last element". */
export function currentEvent(events: readonly InspectionEvent[]): InspectionEvent | null {
  let out: InspectionEvent | null = null;
  for (const ev of events) if (out == null || ev.date > out.date) out = ev;
  return out;
}

/** The earliest inspection on record — the baseline end of the series. */
export function earliestEvent(events: readonly InspectionEvent[]): InspectionEvent | null {
  let out: InspectionEvent | null = null;
  for (const ev of events) if (out == null || ev.date < out.date) out = ev;
  return out;
}
